import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DynamicFields } from "./DynamicFields";
import { AIAssistPanel } from "./AIAssistPanel";

const RefControl = ({ form, set, onDup }) => {
  const [check, setCheck] = useState(null);
  useEffect(() => {
    if (form.ref_mode !== "manual" || !form.ref_no.trim()) { setCheck(null); onDup(false); return; }
    const t = setTimeout(async () => {
      const r = await api.get("/records/check-ref", { params: { ref_no: form.ref_no } });
      setCheck(r.data);
      onDup(r.data.exists || !r.data.valid_format);
    }, 400);
    return () => clearTimeout(t);
  }, [form.ref_no, form.ref_mode]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-600">Reference number</Label>
      <div className="flex gap-2">
        <select value={form.ref_mode} onChange={(e) => set({ ref_mode: e.target.value })} data-testid="ref-mode-select"
          className="h-9 rounded-md border border-slate-300 bg-white text-sm px-2">
          <option value="auto">Auto-assign</option>
          <option value="manual">Existing number</option>
        </select>
        {form.ref_mode === "manual" && (
          <Input value={form.ref_no} onChange={(e) => set({ ref_no: e.target.value.toUpperCase() })} placeholder="COR/2026/00123"
            className="font-mono bg-white" data-testid="ref-no-input" />
        )}
      </div>
      {check && (check.exists ? (
        <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="ref-duplicate-warning"><AlertTriangle className="w-3.5 h-3.5" />{check.ref_no} already exists — cannot save a duplicate.</p>
      ) : !check.valid_format ? (
        <p className="text-xs text-amber-700" data-testid="ref-format-warning">Use the format ABC/2026/00001</p>
      ) : (
        <p className="text-xs text-emerald-700 flex items-center gap-1" data-testid="ref-available"><CheckCircle2 className="w-3.5 h-3.5" />Available</p>
      ))}
      {form.ref_mode === "auto" && <p className="text-xs text-slate-500">Assigned on save, e.g. {form.prefix}/{new Date().getFullYear()}/00001</p>}
    </div>
  );
};

export const RecordForm = ({ mode, initial, attachments, onSave, onCancel, saving }) => {
  const { meta } = useAuth();
  const offices = meta.offices.filter((o) => o.create_types.length);
  const startOffice = offices.find((o) => o.id === initial.office) || offices[0];
  const startType = mode === "edit" ? initial.record_type
    : startOffice?.create_types.includes(initial.record_type) ? initial.record_type : startOffice?.create_types[0];
  const [form, setForm] = useState({ office: startOffice?.id, record_type: startType, ref_mode: "auto", ref_no: "",
    title: initial.title || "", fields: initial.fields || {}, tags: (initial.tags || []).join(", "), summary: initial.summary || "" });
  const [dup, setDup] = useState(false);
  const [hl, setHl] = useState([]);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const creatable = (offices.find((o) => o.id === form.office)?.create_types || []).map((k) => [k, meta.record_types[k]]);
  const typeDef = meta.record_types[form.record_type];

  const applyAI = (s) => {
    const p = {};
    if (s.record_type) { p.record_type = s.record_type; p.fields = {}; }
    if (s.title) p.title = s.title;
    if (s.tags) p.tags = s.tags.join(", ");
    if (s.summary) p.summary = s.summary;
    setForm((f) => ({ ...f, ...p, fields: { ...(p.fields ?? f.fields), ...s.fields } }));
    setHl(Object.keys(s.fields));
  };

  const save = (e) => {
    e.preventDefault();
    onSave({ office: form.office, record_type: form.record_type, title: form.title, fields: form.fields, summary: form.summary,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      ref_no: form.ref_mode === "manual" ? form.ref_no : null });
  };

  return (
    <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
      <form onSubmit={save} className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="record-form">
        {mode === "create" && (
          <section className="p-5 border-b border-slate-200 grid sm:grid-cols-2 gap-4">
            {offices.length > 1 && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-semibold text-slate-600">Office / committee room</Label>
                <select value={form.office} data-testid="record-office-select" className="w-full h-9 rounded-md border border-slate-300 bg-white text-sm px-2"
                  onChange={(e) => { const o = offices.find((x) => x.id === e.target.value); set({ office: o.id, record_type: o.create_types.includes(form.record_type) ? form.record_type : o.create_types[0], fields: o.create_types.includes(form.record_type) ? form.fields : {} }); }}>
                  {offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-600">Record type</Label>
              <select value={form.record_type} onChange={(e) => set({ record_type: e.target.value, fields: {} })} data-testid="record-type-select"
                className="w-full h-9 rounded-md border border-slate-300 bg-white text-sm px-2">
                {creatable.map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
              </select>
            </div>
            <RefControl form={{ ...form, prefix: typeDef.prefix }} set={set} onDup={setDup} />
          </section>
        )}
        <section className="p-5 border-b border-slate-200 space-y-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">General</div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Title / subject<span className="text-rose-600 ml-0.5">*</span></Label>
            <Input value={form.title} onChange={(e) => set({ title: e.target.value })} data-testid="record-title-input" className="bg-white" />
          </div>
        </section>
        <section className="p-5 border-b border-slate-200 space-y-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{typeDef.label} details</div>
          <DynamicFields typeDef={typeDef} values={form.fields} onChange={(fields) => set({ fields })} highlight={hl} />
        </section>
        <section className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Tags (comma separated)</Label>
            <Input value={form.tags} onChange={(e) => set({ tags: e.target.value })} data-testid="record-tags-input" className="bg-white" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-600">Summary</Label>
            <Textarea rows={3} value={form.summary} onChange={(e) => set({ summary: e.target.value })} data-testid="record-summary-input" className="bg-white" />
          </div>
          {typeDef.attachment_required && mode === "create" && (
            <p className="text-xs text-slate-500">This record type needs at least one attachment before it can be submitted. You can upload files after saving the draft.</p>
          )}
          <div className="flex gap-2 justify-end pt-2">
            {onCancel && <Button type="button" variant="outline" onClick={onCancel} data-testid="record-form-cancel">Cancel</Button>}
            <Button type="submit" disabled={saving || dup} data-testid="create-record-submit-button">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{mode === "create" ? "Save draft" : "Save changes"}
            </Button>
          </div>
        </section>
      </form>
      <div className="lg:sticky lg:top-24">
        <AIAssistPanel recordType={form.record_type} attachments={attachments} onApply={applyAI} canChangeType={mode === "create"} />
      </div>
    </div>
  );
};
